import React, { useEffect, useRef, useState } from "react";
import {
  bootstrapCameraKit,
  createMediaStreamSource,
  Transform2D,
} from "@snap/camera-kit";
import logo from "../assets/icon.png";

function CameraKit() {
  // Refs hold mutable objects that persist across renders
  // canvasRef: DOM element where Camera Kit will render
  const canvasRef = useRef(null);
  // sessionRef: holds the Camera Kit session object so we can pause/destroy it later
  const sessionRef = useRef(null);
  // isInitializingRef: guard to prevent concurrent initializations
  const isInitializingRef = useRef(false);
  // streamRef: the MediaStream returned from getUserMedia, so we can stop tracks on cleanup
  const streamRef = useRef(null);

  // status: UI-friendly state for bootstrapping / loading stages
  const [status, setStatus] = useState("initializing");
  // error: human-readable error message to display in overlay
  const [error, setError] = useState(null);

  const [buttonVisible, setButtonVisible] = useState(false);

  const [isRecording, setIsRecording] = useState(false);

  // Debug effect to watch buttonVisible changes
  useEffect(() => {
    console.log("Button visible state changed to:", buttonVisible);
  }, [buttonVisible]);

  const toggleRecording = () => {
    setIsRecording((prev) => !prev);
  };

  // Cleanup routine: pause/destroy Camera Kit session and stop the camera tracks.
  // Call this on unmount or before re-initializing to avoid resource leaks.
  const cleanupSession = async () => {
    if (sessionRef.current) {
      try {
        // Pause rendering then destroy the session object
        await sessionRef.current.pause();
        sessionRef.current.destroy();
        sessionRef.current = null;
      } catch (error) {
        console.error("Error cleaning up session:", error);
      }
    }

    // Stop all tracks of the MediaStream to release the camera
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  // Resize canvas to fill its container / viewport and account for devicePixelRatio.
  // This ensures the canvas backing store (canvas.width/height) matches the displayed
  // CSS size multiplied by devicePixelRatio so drawing appears sharp on high-DPI screens.
  const resizeCanvas = (canvas) => {
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;

    // Use the canvas's client size (derived from CSS) so responsive styles are respected.
    const clientWidth = canvas.clientWidth || window.innerWidth;
    const clientHeight = canvas.clientHeight || window.innerHeight;

    // Set the backing store size to clientSize * dpr for crisp rendering.
    canvas.width = Math.max(1, Math.floor(clientWidth * dpr));
    canvas.height = Math.max(1, Math.floor(clientHeight * dpr));

    // Keep the CSS size at 100% so layout doesn't change when we modify the backing store.
    canvas.style.width = "100%";
    canvas.style.height = "100%";
  };

  // Ensure the canvas resizes when the window size or orientation changes.
  // We run an initial resize immediately and attach event listeners for the session lifetime.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Initial resize so the backing store is correct before the session is created.
    resizeCanvas(canvas);

    const onResize = () => resizeCanvas(canvas);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // Main initialization flow for Camera Kit.
  // Steps:
  //  - parse lensId/groupId from URL
  //  - prevent double-initialization
  //  - bootstrap the SDK
  //  - size the canvas for DPR
  //  - create a Camera Kit session with the canvas as the render target
  //  - request a camera MediaStream sized to match the canvas backing store
  //  - set the MediaStream as the session source, load/apply the lens and start playback
  const initializeCameraKit = async () => {
    const url = window.location.href;

    // Parse lens configuration from the query string
    const urlParams = new URLSearchParams(window.location.search);
    console.log("URL Parameters:", Array.from(urlParams.entries()));
    const lensId = urlParams.get("lensId");
    const groupId = urlParams.get("groupId");
    console.log("Lens ID from URL:", lensId);
    console.log("Group ID from URL:", groupId);

    // Validate required parameters
    if (!lensId || !groupId) {
      setError("Missing lensId or groupId in URL parameters");
      setStatus("error");
      return;
    }

    console.log("Current URL:", url);
    setStatus("initializing");

    // Prevent concurrent init calls
    if (isInitializingRef.current || sessionRef.current) {
      console.log("Camera Kit already initializing or initialized");
      return;
    }

    isInitializingRef.current = true;

    try {
      // API token for Camera Kit SDK (replace with secure injection in production)
      const apiToken =
        "eyJhbGciOiJIUzI1NiIsImtpZCI6IkNhbnZhc1MyU0hNQUNQcm9kIiwidHlwIjoiSldUIn0.eyJhdWQiOiJjYW52YXMtY2FudmFzYXBpIiwiaXNzIjoiY2FudmFzLXMyc3Rva2VuIiwibmJmIjoxNzU5MzQ4MjYyLCJzdWIiOiJiYzk2MDcyMS00MzI2LTRkMmQtOTA3Zi1lZjg5ZWYzMWY4OGR-U1RBR0lOR35kMTE2NDUyMC1jMjBkLTQzNzQtYTY5Ny02NjJjOWQ3NjEwYzcifQ.TCZX88ViTstE28NqkYKpndgIqxYW8wGUjYuofWVV3UQ";

      console.log("🚀 Initializing Camera Kit SDK...");
      console.log(
        "📋 API Token (first 20 chars):",
        apiToken.substring(0, 20) + "..."
      );

      setStatus("bootstrapping");
      // Bootstrap the SDK (network calls may happen inside)
      const cameraKit = await bootstrapCameraKit({
        apiToken,
        // Helpful debug logging from the SDK
        logger: {
          level: "debug",
        },
      });

      // Ensure we have a mounted canvas element to attach Camera Kit to
      const canvas = canvasRef.current;
      if (!canvas) {
        isInitializingRef.current = false;
        return;
      }

      // Size the canvas backing store for DPR before creating the session
      setStatus("resizing-canvas");
      resizeCanvas(canvas);

      // Create the Camera Kit session that will render into our canvas
      setStatus("creating-session");
      const session = await cameraKit.createSession({
        liveRenderTarget: canvas,
      });
      sessionRef.current = session;

      setStatus("session-created");

      // Attach generic error listener from SDK events
      session.events.addEventListener("error", (event) => {
        if (event.detail.error.name === "LensExecutionError") {
          console.log(
            "The current Lens encountered an error and was removed.",
            event.detail.error
          );
        }
      });

      // Request camera with ideal resolution matching the canvas backing store.
      setStatus("getting-camera");
      const dpr = window.devicePixelRatio || 1;
      const targetWidth = Math.max(
        640,
        Math.floor((canvas.clientWidth || window.innerWidth) * dpr)
      );
      const targetHeight = Math.max(
        480,
        Math.floor((canvas.clientHeight || window.innerHeight) * dpr)
      );

      const constraints = {
        video: {
          facingMode: "environment", // Back camera
          // Remove specific width/height constraints to avoid cropping
          // Let the camera use its natural resolution
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      setStatus("camera-acquired");

      // Wrap the MediaStream in a Camera Kit source and attach it to the session
      const source = createMediaStreamSource(stream, {
        // Remove MirrorX transform for back camera - it's not needed and can cause issues
        cameraType: "back", // Match the facingMode: "environment" setting
      });
      await session.setSource(source);
      setStatus("source-set");

      // Load and apply the lens specified by the URL parameters
      setStatus("loading-lens");
      const lens = await cameraKit.lensRepository.loadLens(lensId, groupId);
      setStatus("lens-loaded");
      await session.applyLens(lens);
      setStatus("applying-lens");

      // Set button visible immediately for testing
      console.log("Setting button visible to true immediately");
      setButtonVisible(true);

      // Start the session playback loop
      setStatus("starting");
      await session.play();
      console.log("Lens rendering has started!");
      setStatus("running");
    } catch (error) {
      console.error("Error initializing Camera Kit:", error);
      // Update UI so the overlay shows an error state
      setError(error.message || "Initialization failed");
      setStatus("error");

      // Helpful debug logging for common failure modes
      if (error.message && error.message.includes("401")) {
        console.error("Authentication failed - check your API token");
      }
      if (
        error.message &&
        error.message.includes("transferControlToOffscreen")
      ) {
        console.error(
          "Canvas control transfer failed - canvas may already be in use"
        );
      }
    } finally {
      // Always clear the initialization guard when done
      isInitializingRef.current = false;
    }
  };

  useEffect(() => {
    // Add a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      initializeCameraKit();
    }, 100);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      cleanupSession();
    };
  }, []);

  return (
    <div>
      <div className="frame">
        <div className="header">
          <div className="logoContainer">
            <img src={logo} alt="TinyAR Logo" className="logo" />
            <div className="headerText">TinyAR !</div>
          </div>
          <div className="burgerMenu">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <div className="ck_container">
          {/* Loading overlay */}
          {status !== "running" && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#3e3e3eff",
                flexDirection: "column",
                padding: 20,
                pointerEvents: "none",
              }}
            >
              <img
                src={logo}
                alt="TinyAR Logo"
                className="logo"
                style={{ minWidth: "100px" }}
              />
              <div style={{ fontSize: 36, marginBottom: 12 }}>
                {status === "error" ? "Error" : "Loading..."}
              </div>
              <div style={{ fontSize: 14, opacity: 0.9 }}>{status}</div>
              {error && (
                <div
                  style={{
                    marginTop: 12,
                    color: "#1d1d1dff",
                    maxWidth: "80%",
                    textAlign: "center",
                  }}
                >
                  {error}
                </div>
              )}
            </div>
          )}
          <div className="ck_UI">
            <button
              className={`recordingButton ${isRecording ? "active" : ""} ${
                buttonVisible ? "visible" : "hidden"
              }`}
              onClick={toggleRecording}
            >
              <div className="innerCircle">
                {isRecording ? (
                  <div className="stopIcon"></div>
                ) : (
                  <div className="playIcon"></div>
                )}
              </div>
            </button>
          </div>
          <canvas
            ref={canvasRef}
            id="my-canvas"
            aria-label="Camera preview"
            className="ck_video"
          ></canvas>
        </div>
      </div>
    </div>
  );
}

export default CameraKit;
